import { useState, useMemo, type CSSProperties, type FormEvent } from 'react'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useAuth } from '@/hooks/useAuth'
import Modal from '@/components/layout/Modal'
import { showToast } from '@/components/layout/Toast'
import type { AppCatalogo, EstadoApp, ModeloCobro, TarifaModelo } from '@/lib/types'
import { ESTADO_META, ESTADOS_APP, cobroResumen } from './appsShared'
import { IconPlus, IconEdit, IconTrash, IconX, IconExternalLink, IconSearch } from '@/components/icons/Icons'
import { PageHeader } from '@/components/ui'

interface AppForm {
  slug: string
  nombre: string
  descripcion: string
  descripcion_larga: string
  endpoint: string
  estado: EstadoApp
  etiquetas: string[]
  modelo_cobro: ModeloCobro
}

const emptyForm: AppForm = {
  slug: '', nombre: '', descripcion: '', descripcion_larga: '', endpoint: '',
  estado: 'produccion', etiquetas: [], modelo_cobro: { tipo: 'por_documento', creditos: 0, unidad: 'uso', descripcion: '' },
}

function slugify(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function AppsCatalogo() {
  const { role } = useAuth()
  const isAdmin = role === 'admin'
  const { data: apps = [], isLoading } = useCachedQuery<AppCatalogo>('apps_catalogo')
  const { addMut, updateMut, removeMut } = useCachedMutations('apps_catalogo')

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [fEtiquetas, setFEtiquetas] = useState<string[]>([])

  const [modal, setModal] = useState(false)
  const [edit, setEdit] = useState<AppCatalogo | null>(null)
  const [form, setForm] = useState<AppForm>(emptyForm)
  const [tagIn, setTagIn] = useState('')
  const [saving, setSaving] = useState(false)
  const [del, setDel] = useState<AppCatalogo | null>(null)

  const selected = selectedId != null ? apps.find(a => a.id === selectedId) ?? null : null

  const allEtiquetas = useMemo(() => {
    const set = new Set<string>()
    apps.forEach(a => (Array.isArray(a.etiquetas) ? a.etiquetas : []).forEach(t => set.add(t)))
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [apps])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return apps.filter(a => {
      if (q && !(a.nombre.toLowerCase().includes(q) || (a.descripcion || '').toLowerCase().includes(q))) return false
      const et = Array.isArray(a.etiquetas) ? a.etiquetas : []
      if (fEtiquetas.length && !fEtiquetas.every(t => et.includes(t))) return false
      return true
    })
  }, [apps, search, fEtiquetas])

  /* ─── form helpers ─── */
  const openNew = () => { setEdit(null); setForm({ ...emptyForm }); setTagIn(''); setModal(true) }
  const openEdit = (a: AppCatalogo) => {
    setEdit(a)
    setForm({
      slug: a.slug, nombre: a.nombre, descripcion: a.descripcion ?? '', descripcion_larga: a.descripcion_larga ?? '',
      endpoint: a.endpoint ?? '', estado: a.estado, etiquetas: Array.isArray(a.etiquetas) ? a.etiquetas : [],
      modelo_cobro: a.modelo_cobro && Object.keys(a.modelo_cobro).length > 0 ? a.modelo_cobro : { tipo: 'por_documento', creditos: 0, unidad: 'uso' },
    })
    setTagIn(''); setModal(true)
  }
  const addTag = () => { const t = tagIn.trim(); if (t && !form.etiquetas.includes(t)) { setForm(f => ({ ...f, etiquetas: [...f.etiquetas, t] })); setTagIn('') } }
  const rmTag = (t: string) => setForm(f => ({ ...f, etiquetas: f.etiquetas.filter(x => x !== t) }))

  const setCobro = (patch: Partial<ModeloCobro>) => setForm(f => ({ ...f, modelo_cobro: { ...f.modelo_cobro, ...patch } }))
  const addTarifa = () => setCobro({ tarifas: [...(form.modelo_cobro.tarifas ?? []), { modelo: '', creditos: 0, unidad: '1k tokens' }] })
  const setTarifa = (i: number, patch: Partial<TarifaModelo>) => setCobro({ tarifas: (form.modelo_cobro.tarifas ?? []).map((t, idx) => idx === i ? { ...t, ...patch } : t) })
  const rmTarifa = (i: number) => setCobro({ tarifas: (form.modelo_cobro.tarifas ?? []).filter((_, idx) => idx !== i) })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!form.nombre.trim()) { showToast('Nombre requerido', 'error'); return }
    const slug = (form.slug.trim() || slugify(form.nombre))
    if (!slug) { showToast('Slug requerido', 'error'); return }
    setSaving(true)
    try {
      const payload = { ...form, slug }
      if (edit) {
        await updateMut.mutateAsync({ id: edit.id, ...payload } as Record<string, unknown>)
        showToast('App actualizada', 'success')
      } else {
        const created = await addMut.mutateAsync(payload as unknown as Record<string, unknown>) as AppCatalogo | undefined
        if (created?.id) setSelectedId(created.id)
        showToast('App creada', 'success')
      }
      setModal(false)
    } catch (err) {
      const msg = err instanceof Error && /409|slug/i.test(err.message) ? 'Ya existe una app con ese slug' : 'Error al guardar'
      showToast(msg, 'error')
    } finally { setSaving(false) }
  }

  const doDelete = async () => {
    if (!del) return
    try {
      await removeMut.mutateAsync(del.id)
      showToast('App eliminada', 'success')
      if (selectedId === del.id) setSelectedId(null)
      setDel(null)
    } catch { showToast('Error', 'error') }
  }

  if (isLoading) return <div style={{ color: 'var(--text-muted)', padding: '24px 0', textAlign: 'center' }}>Cargando apps…</div>

  return (
    <>
      {selected ? renderDetail() : renderList()}

      {/* Modal alta / edición */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={edit ? 'Editar App' : 'Nueva App'}>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Nombre *</label><input className="form-input" value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} required autoFocus /></div>
            <div className="form-group"><label className="form-label">Slug</label><input className="form-input" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder={slugify(form.nombre) || 'auto'} /></div>
          </div>
          <div className="form-group"><label className="form-label">Resumen corto</label><textarea className="form-textarea" rows={2} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Una línea (se muestra en la tabla)." /></div>
          <div className="form-group"><label className="form-label">Descripción larga</label><textarea className="form-textarea" rows={3} value={form.descripcion_larga} onChange={e => setForm(f => ({ ...f, descripcion_larga: e.target.value }))} placeholder="Detalle que se ve en la vista de la app." /></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Endpoint</label><input className="form-input" value={form.endpoint} onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))} placeholder="https://raglink.1conecta.com/v1/..." /></div>
            <div className="form-group"><label className="form-label">Estado</label>
              <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value as EstadoApp }))}>
                {ESTADOS_APP.map(s => <option key={s} value={s}>{ESTADO_META[s].label}</option>)}
              </select>
            </div>
          </div>

          {/* Etiquetas */}
          <div className="form-group">
            <label className="form-label">Etiquetas</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" value={tagIn} onChange={e => setTagIn(e.target.value)} placeholder="Ej: ia, documentos" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} />
              <button type="button" className="btn btn-secondary btn-sm" onClick={addTag}>+</button>
            </div>
            {form.etiquetas.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>{form.etiquetas.map(t => <span key={t} className="badge" style={{ cursor: 'pointer', background: 'var(--bg-secondary)', border: '1px solid var(--border)' }} onClick={() => rmTag(t)}>{t} ×</span>)}</div>}
          </div>

          {/* Modelo de cobro */}
          <div className="form-group" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
            <label className="form-label" style={{ marginBottom: 8 }}>Modelo de cobro</label>
            <div className="form-group">
              <select className="form-select" value={form.modelo_cobro.tipo ?? 'otro'} onChange={e => setCobro({ tipo: e.target.value as ModeloCobro['tipo'] })}>
                <option value="por_documento">Por documento</option>
                <option value="por_token">Por token</option>
                <option value="por_modelo">Por modelo (tarifario)</option>
                <option value="suscripcion">Suscripción</option>
                <option value="otro">Otro</option>
              </select>
            </div>
            {form.modelo_cobro.tipo === 'por_modelo' ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 6px' }}>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Tarifas por modelo</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addTarifa}><IconPlus size={13} /> Tarifa</button>
                </div>
                {(form.modelo_cobro.tarifas ?? []).length === 0 && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0 }}>Sin tarifas.</p>}
                {(form.modelo_cobro.tarifas ?? []).map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input className="form-input" style={{ flex: 2 }} value={t.modelo} onChange={e => setTarifa(i, { modelo: e.target.value })} placeholder="modelo (gemini-2.5-flash)" />
                    <input className="form-input" style={{ flex: 1 }} type="number" step="0.01" value={t.creditos} onChange={e => setTarifa(i, { creditos: parseFloat(e.target.value) || 0 })} placeholder="créditos" />
                    <input className="form-input" style={{ flex: 1 }} value={t.unidad} onChange={e => setTarifa(i, { unidad: e.target.value })} placeholder="unidad" />
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => rmTarifa(i)}><IconX size={13} /></button>
                  </div>
                ))}
              </>
            ) : (
              <div className="form-row">
                <div className="form-group"><label className="form-label">Créditos</label><input className="form-input" type="number" step="0.01" min="0" value={form.modelo_cobro.creditos ?? 0} onChange={e => setCobro({ creditos: parseFloat(e.target.value) || 0 })} /></div>
                <div className="form-group"><label className="form-label">Unidad</label><input className="form-input" value={form.modelo_cobro.unidad ?? ''} onChange={e => setCobro({ unidad: e.target.value })} placeholder="documento, 1k tokens…" /></div>
              </div>
            )}
            <div className="form-group" style={{ marginTop: 6 }}><label className="form-label">Descripción del cobro</label><input className="form-input" value={form.modelo_cobro.descripcion ?? ''} onChange={e => setCobro({ descripcion: e.target.value })} placeholder="Ej: 3 créditos por documento" /></div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando…' : edit ? 'Actualizar' : 'Crear'}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar App">
        <p>¿Eliminar la app <strong>{del?.nombre}</strong> del catálogo?</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={doDelete}>Eliminar</button>
        </div>
      </Modal>
    </>
  )

  /* ═══════════ LIST (tabla) ═══════════ */
  function renderList() {
    const chipToggle = (v: string) => setFEtiquetas(a => a.includes(v) ? a.filter(x => x !== v) : [...a, v])
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{filtered.length} de {apps.length} apps</span>
          {isAdmin && <button className="btn btn-primary" onClick={openNew}><IconPlus size={16} /> Nueva App</button>}
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
          <div style={{ position: 'relative', maxWidth: 380 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex' }}><IconSearch size={16} /></span>
            <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Buscar app…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {allEtiquetas.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Etiquetas</span>
              {allEtiquetas.map(t => {
                const on = fEtiquetas.includes(t)
                return <button key={t} onClick={() => chipToggle(t)} className="badge" style={{ cursor: 'pointer', background: on ? 'var(--primary)' : 'var(--bg-secondary)', color: on ? 'var(--text-on-primary)' : 'var(--text-secondary)', border: '1px solid var(--border)' }}>{t}</button>
              })}
              {(fEtiquetas.length > 0 || search) && <button className="btn btn-secondary btn-sm" onClick={() => { setFEtiquetas([]); setSearch('') }}>Limpiar</button>}
            </div>
          )}
        </div>

        {/* Tabla */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'auto', background: 'var(--bg-primary)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 900 }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left' }}>
                <th style={th}>App</th>
                <th style={th}>Estado</th>
                <th style={th}>Etiquetas</th>
                <th style={th}>Endpoint</th>
                <th style={th}>Cobro</th>
                <th style={{ ...th, textAlign: 'center' }}>Cuentas</th>
                <th style={{ ...th, textAlign: 'center' }}>Consumo</th>
                {isAdmin && <th style={{ ...th, textAlign: 'right' }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={isAdmin ? 8 : 7} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Sin apps que coincidan.</td></tr>
              ) : filtered.map(a => {
                const et = Array.isArray(a.etiquetas) ? a.etiquetas : []
                const meta = ESTADO_META[a.estado] || ESTADO_META.produccion
                return (
                  <tr key={a.id} onClick={() => setSelectedId(a.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{a.nombre}</div>
                      {a.descripcion && <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.descripcion}</div>}
                    </td>
                    <td style={td}><span className="badge" style={{ background: meta.bg, color: meta.color, fontSize: '0.66rem', fontWeight: 700 }}>{meta.label}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {et.slice(0, 3).map(t => <span key={t} className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', fontSize: '0.68rem' }}>{t}</span>)}
                        {et.length > 3 && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>+{et.length - 3}</span>}
                        {et.length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </div>
                    </td>
                    <td style={{ ...td, maxWidth: 200 }}>{a.endpoint ? <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{a.endpoint.replace(/^https?:\/\//, '')}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ ...td, fontSize: '0.8rem' }}>{cobroResumen(a.modelo_cobro)}</td>
                    <td style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>—</td>
                    <td style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>—</td>
                    {isAdmin && (
                      <td style={{ ...td, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(a)}><IconEdit size={14} /></button>
                          <button className="btn btn-danger btn-sm" onClick={() => setDel(a)}><IconTrash size={14} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  /* ═══════════ DETAIL ═══════════ */
  function renderDetail() {
    if (!selected) return null
    const et = Array.isArray(selected.etiquetas) ? selected.etiquetas : []
    const meta = ESTADO_META[selected.estado] || ESTADO_META.produccion
    const mc = selected.modelo_cobro || {}
    return (
      <>
        <PageHeader
          breadcrumbs={[
            { label: 'Apps', onClick: () => setSelectedId(null) },
            { label: selected.nombre },
          ]}
          meta={<span className="badge" style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}>{meta.label}</span>}
          actions={isAdmin && (
            <>
              <button className="btn btn-primary btn-sm" onClick={() => openEdit(selected)}><IconEdit size={14} /> Editar</button>
              <button className="btn btn-danger btn-sm" onClick={() => setDel(selected)}><IconTrash size={14} /></button>
            </>
          )}
        />

        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <span className="badge" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.72rem' }}>{selected.slug}</span>
            {et.map(t => <span key={t} className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{t}</span>)}
          </div>
          {selected.descripcion_larga
            ? <p style={{ color: 'var(--text-secondary)', marginTop: 12, maxWidth: 820, lineHeight: 1.6 }}>{selected.descripcion_larga}</p>
            : selected.descripcion && <p style={{ color: 'var(--text-secondary)', marginTop: 12, maxWidth: 820, lineHeight: 1.6 }}>{selected.descripcion}</p>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, maxWidth: 900 }}>
          {/* Endpoint */}
          <section style={panel}>
            <h3 style={panelTitle}>Endpoint</h3>
            {selected.endpoint
              ? <a href={selected.endpoint} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--primary)', textDecoration: 'none', fontSize: '0.85rem', wordBreak: 'break-all' }}><IconExternalLink size={13} /> {selected.endpoint}</a>
              : <span style={{ color: 'var(--text-muted)' }}>Sin endpoint definido.</span>}
          </section>

          {/* Modelo de cobro */}
          <section style={panel}>
            <h3 style={panelTitle}>Modelo de cobro</h3>
            {mc.tipo === 'por_modelo' && Array.isArray(mc.tarifas) && mc.tarifas.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}><th style={{ padding: '4px 6px', fontWeight: 600 }}>Modelo</th><th style={{ padding: '4px 6px', fontWeight: 600, textAlign: 'right' }}>Créditos</th><th style={{ padding: '4px 6px', fontWeight: 600 }}>Unidad</th></tr></thead>
                <tbody>
                  {mc.tarifas.map((t, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '5px 6px', fontFamily: 'monospace' }}>{t.modelo}</td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{t.creditos}</td>
                      <td style={{ padding: '5px 6px', color: 'var(--text-secondary)' }}>{t.unidad}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: '0.9rem' }}>
                <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.05rem' }}>{cobroResumen(mc)}</div>
                {mc.descripcion && <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '6px 0 0' }}>{mc.descripcion}</p>}
              </div>
            )}
          </section>

          {/* Consumo / cuentas (placeholder) */}
          <section style={panel}>
            <h3 style={panelTitle}>Consumo y cuentas</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: 0, lineHeight: 1.5 }}>
              Métricas en vivo pendientes de la API de reportería del gateway (equipo DOCLING-SERVICE).
            </p>
            <div style={{ display: 'flex', gap: 18, marginTop: 12 }}>
              <div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-muted)' }}>—</div><div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Cuentas</div></div>
              <div><div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-muted)' }}>—</div><div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Consumo</div></div>
            </div>
          </section>
        </div>
      </>
    )
  }
}

/* ─── styles ─── */
const th: CSSProperties = { padding: '11px 16px', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)' }
const td: CSSProperties = { padding: '11px 16px', verticalAlign: 'middle' }
const panel: CSSProperties = { border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-primary)' }
const panelTitle: CSSProperties = { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', margin: '0 0 10px' }
